import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Form,
  Input,
  Button,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createConditionData } from "services/_conditions";
import { successToast } from "components/toast";
import { errorToast } from "components/toast";
import { useNavigate, useParams } from "react-router-dom";
import { getConditionDataById } from "services/_conditions";
import { updateConditionDataById } from "services/_conditions";
import TextEditor from "components/TextEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateMedicalCondition = () => {
  const { id } = useParams();
  const [editData, setEditData] = useState({});
  const [media, setMedia] = useState({});
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/admin/medical-condition");
  };

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      name: values.name,
      short_description: values.short_description,
      status: values.status,
      detailed_content: values.detailed_content,
      media: media,
    };

    if (id) {
      const data = await updateConditionDataById(id, payload);
      if (data.data) {
        successToast(data.message);
        resetForm();
        navigate("/admin/medical-condition");
      } else {
        errorToast(data.message);
      }
    } else {
      const data = await createConditionData(payload);
      if (data.data) {
        successToast(data.message);
        resetForm();
        navigate("/admin/medical-condition");
      } else {
        errorToast(data.message);
      }
    }
  };

  const getInitialData = useCallback(async () => {
    const data = await getConditionDataById(id);
    if (data.data) {
      setEditData(data.data);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getInitialData();
    }
  }, [getInitialData, id]);

  const imgUrl = editData?.image_url;

  const MedicalConditionSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    short_description: Yup.string().required("Short description is required"),
    image: Yup.mixed().nullable(),
    status: Yup.boolean().nullable(),
    detailed_content: Yup.string().optional(),
  });

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-1">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                      <i
                        className="fas fa-arrow-left"
                        style={{ paddingRight: "10px" }}
                        onClick={handleNavigate}
                      ></i>
                      {id
                        ? "Edit Medical Condition"
                        : "Create Medical Condition "}
                    </h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    name: editData?.name || "",
                    short_description: editData?.short_description || "",
                    image: null,
                    status: editData?.status ?? null,
                    detailed_content: editData?.detailed_content || "",
                  }}
                  validationSchema={MedicalConditionSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, handleChange, handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="pl-lg-4">
                        <Row>
                          {/* Name */}
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-name"
                              >
                                Name <span style={{ color: "red" }}>*</span>
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-name"
                                name="name"
                                placeholder="Enter name"
                                type="text"
                                value={values.name}
                                onChange={handleChange}
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>

                          {/* Short Description */}
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-short_description"
                              >
                                Short Description
                                <span style={{ color: "red" }}> *</span>
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-short_description"
                                name="short_description"
                                placeholder="Enter short description"
                                type="text"
                                value={values.short_description}
                                onChange={handleChange}
                              />
                              <ErrorMessage
                                name="short_description"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="12">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-name"
                              >
                                Detailed Content{" "}
                              </label>
                              <div className="custom-quill">
                                <TextEditor
                                  value={values.detailed_content}
                                  onChange={(val) =>
                                    setFieldValue("detailed_content", val)
                                  }
                                  placeholder="Enter detailed content..."
                                  height={400}
                                />
                              </div>
                              <ErrorMessage
                                name="detailed_content"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-status"
                              >
                                Status
                              </label>
                              <Input
                                type="select"
                                id="input-status"
                                name="status"
                                className="form-control-alternative"
                                style={{
                                  boxShadow:
                                    "0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)",
                                  border: 0,
                                  color: "#90a4ae !important",
                                }}
                                value={
                                  values.status === true
                                    ? "true"
                                    : values.status === false
                                    ? "false"
                                    : ""
                                }
                                onChange={(e) =>
                                  setFieldValue(
                                    "status",
                                    e.target.value === "true" ? true : false
                                  )
                                }
                              >
                                <option value="">Select status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </Input>
                              <ErrorMessage
                                name="status"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>

                          {/* Image Upload */}
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-image"
                              >
                                Image Upload
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-image"
                                name="image"
                                type="file"
                                style={{
                                  color: "#90a4ae",
                                  backgroundColor: "white",
                                  boxShadow:
                                    "0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)",
                                  border: 0,
                                  height: "43px",
                                  padding: "5px 0 5px 5px",
                                  margin: "0",
                                  lineHeight: "28px",
                                  borderRadius: "5px",
                                  overflow: "hidden",
                                }}
                                onChange={(event) => {
                                  const file = event.currentTarget.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                      const base64Raw =
                                        reader.result.split(",")[1];
                                      const fileObj = {
                                        fileName: file.name,
                                        fileData: base64Raw,
                                      };
                                      setMedia(fileObj);
                                      setFieldValue("image", file);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="image"
                                component="div"
                                className="text-danger"
                              />

                              {id ? (
                                <div className="mt-2">
                                  <img
                                    src={`${BACKEND_URL}${imgUrl}`}
                                    alt="Preview"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>

                      {/* Submit */}
                      <div className="pl-lg-4">
                        <Button color="primary" type="submit">
                          Save
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CreateMedicalCondition;
